import { nanoid } from "nanoid";
import { v4 as uuid } from "uuid";

import {
  COLOR_THUMBNAILS_ORDER_RULES,
  MEDIA_SLIDER_ORDER_RULES,
  PACK_SHOT_001,
  PACK_SHOT_002,
  PRODUCT_ROW_SIZE,
  PackType,
  RECOMMENDATION_WIDGET_ORDER_RULES,
  WIDGET_TITLE_MAP,
} from "@is/constants";
import {
  ColorVariant,
  ContentItem,
  ContentItemType,
  HowToWearItWidget,
  MediaOrderingRule,
  MediaType,
  PreferenceOption,
  ProductContentItem,
  ProductItem,
  ProductRowContentItem,
  SearchTerm,
  SearchTermType,
  Section,
  WidgetItem,
  WidgetType,
} from "@is/types";
import { DefaultContentWidget, HTWIMedia } from "@is/types/content";
import {
  chunkArray,
  getStyleBestMatchFlag,
  getStylePriceRange,
} from "@is/utils";

type defaultWidgetProps = {
  type: WidgetType;
  items: ProductItem[];
  attributes?: Partial<WidgetItem>;
  customTitle?: string;
};

type HTWIWidgetProps = {
  type: WidgetType.HowToWearIt;
  items: HowToWearItWidget[];
  attributes?: Partial<WidgetItem>;
  customTitle?: string;
};

type Props = defaultWidgetProps | HTWIWidgetProps;

export const createWidgetItem = ({
  type,
  items = [],
  attributes = {},
  customTitle,
}: Props): WidgetItem => {
  let preparedItems: HowToWearItWidget[] | ProductItem[] = [];

  if (type === WidgetType.HowToWearIt) {
    preparedItems = items.map((howToWearItWidget) => ({
      ...howToWearItWidget,
      items: howToWearItWidget.items.map((item) => ({
        ...item,
        widgetProductItemId: nanoid(),
      })),
    }));
  } else {
    preparedItems = items.map((item) => ({
      ...item,
      widgetProductItemId: nanoid(),
    }));
  }

  return {
    id: nanoid(),
    type,
    title: customTitle ? customTitle : WIDGET_TITLE_MAP[type],
    isClosed: false,
    isExpanded: false,
    showAllItems: false,
    items: preparedItems,
    ...attributes,
  } as DefaultContentWidget;
};

export const createContentWidgetItem = ({
  type,
  items = [],
}: {
  type: WidgetType;
  items: ProductItem[];
}): ContentItem => ({
  type: ContentItemType.WidgetItem,
  id: nanoid(),
  content: createWidgetItem({ type, items }),
});

export const convertToSectionHeader = (searchTerms: SearchTerm[]) => {
  const { groups, typedGroup } = searchTerms.reduce(
    ({ groups, typedGroup }, item) => {
      if (item.type === SearchTermType.Text) {
        typedGroup.push({ ...item, key: "text" });
      } else {
        groups.push(item);
      }

      return { groups, typedGroup };
    },
    { groups: [], typedGroup: [] }
  );

  const groupByType: { [key: string]: SearchTerm[] } = [
    ...groups,
    ...typedGroup,
  ].reduce((groups, item) => {
    const { key } = item;

    if (!groups[key]) {
      groups[key] = [];
    }

    groups[key].push(item);
    return groups;
  }, {});

  let result = "";

  Object.values(groupByType).forEach((item) => {
    const values = item.map((item) => item.label);

    const valueString = values.join(", ");

    result += `${valueString} / `;
  });

  const slashPositionFromResult = -3;

  result = result.slice(0, slashPositionFromResult);

  return result;
};

export const searchForScannedSection = (
  sections: Section[],
  scannedValue: string
) => {
  let isNew = false;

  let section = sections.find(
    (section) =>
      section.searchTerms.length === 1 &&
      section.searchTerms[0].value === scannedValue
  );

  if (!section) {
    isNew = true;

    section = {
      id: uuid(),
      items: [],
      searchTerms: [
        {
          type: SearchTermType.Scanned,
          value: scannedValue,
          key: scannedValue,
          label: scannedValue,
        },
      ],
      page: 1,
      totalCount: 1,
      notViewedItems: [],
      hasNextPage: false,
    };
  }

  return { isNew, section };
};

export const getProductMediaIndex = (
  media: MediaType,
  rule: MediaOrderingRule
) => {
  let orderingRules = MEDIA_SLIDER_ORDER_RULES;
  if (rule === MediaOrderingRule.ColorThumbnails) {
    orderingRules = COLOR_THUMBNAILS_ORDER_RULES;
  } else if (rule === MediaOrderingRule.Widget) {
    orderingRules = RECOMMENDATION_WIDGET_ORDER_RULES;
  }

  for (let i = 0; i < orderingRules.length; i++) {
    const order = orderingRules[i];
    const mediaType = media.type?.toLocaleLowerCase();
    const mediaPerspective = media.perspective?.toLocaleLowerCase();
    if (order.type === mediaType && order.perspective === mediaPerspective) {
      return i;
    }
    if (order.type === mediaType && !order.perspective) {
      return i;
    }
  }
  return orderingRules.length;
};

export const sortProductMedia = (
  medias: MediaType[],
  rule: MediaOrderingRule = MediaOrderingRule.MediaSlider
) => {
  let mainSortedMedia = medias;

  // sorting for main Brand
  if (rule === MediaOrderingRule.MediaSlider) {
    const filteredMedias = medias?.filter(
      (media) =>
        media?.url?.includes(PACK_SHOT_001) ||
        media?.url?.includes(PACK_SHOT_002)
    );

    const newMedias = medias?.filter(
      (media) =>
        !media?.url?.includes(PACK_SHOT_001) &&
        !media?.url?.includes(PACK_SHOT_002)
    );

    mainSortedMedia = newMedias?.concat(filteredMedias);
  } else if (rule === MediaOrderingRule.ColorThumbnails) {
    const filteredMedias = medias?.filter((media) =>
      media?.url?.includes(PACK_SHOT_001)
    );

    const filteredSecondMedias = medias?.filter((media) =>
      media?.url?.includes(PACK_SHOT_002)
    );

    const newMedias = medias?.filter(
      (media) =>
        !media?.url?.includes(PACK_SHOT_001) &&
        !media?.url?.includes(PACK_SHOT_002)
    );

    mainSortedMedia = filteredMedias?.concat(filteredSecondMedias, newMedias);
  }

  // sorting by sort ordering rules
  mainSortedMedia.sort(
    (a, b) => getProductMediaIndex(a, rule) - getProductMediaIndex(b, rule)
  );

  return mainSortedMedia;
};

export const getMainImageUrl = (
  medias: MediaType[],
  rule: MediaOrderingRule = MediaOrderingRule.MediaSlider
) => {
  if (!medias.length) {
    return "";
  }

  const media = sortProductMedia(medias, rule);

  return media[0]?.url ?? "";
};

const isSearchTermSelected = (searchTerms, option) =>
  searchTerms.some(
    (searchTerm) =>
      searchTerm.type === option.type &&
      searchTerm.value.toLowerCase() === option.key.toLowerCase()
  );

export const getSelectedTab = (
  preferences: PreferenceOption[],
  searchTerms: SearchTerm[]
) => {
  const selectedTab = preferences?.find((preference) => {
    const options = preference?.options;

    return options.every((option) => {
      const isSelected = isSearchTermSelected(searchTerms, option);

      const subOptions = option?.options || [];

      const isSubcategorySelected = subOptions.length
        ? subOptions.some((option) => isSearchTermSelected(searchTerms, option))
        : true;

      return !isSelected || !isSubcategorySelected;
    });
  });

  return selectedTab?.key;
};

export const getSizesAndLengthsOfSelectedColorVariant = (
  colorVariant: ColorVariant,
  size?: string
) => {
  const colorVariantSizeInfos = colorVariant?.sizeInfos ?? [];

  if (!colorVariantSizeInfos.length) {
    return { sizes: [], lengths: [] };
  }

  return colorVariantSizeInfos.reduce(
    (acc, item, index) => {
      if (item.size && !acc.sizes.includes(item.size)) {
        acc.sizes.push(item.size);
      }

      if (
        size &&
        size === item.size &&
        item.length &&
        !acc.lengths.includes(item.length)
      ) {
        acc.lengths.push(item.length);
      }

      if (!size && item.length && !acc.lengths.includes(item.length)) {
        acc.lengths.push(item.length);
      }

      if (index === colorVariantSizeInfos.length - 1) {
        const sortedLengths = acc.lengths ?? [];
        sortedLengths.sort((a, b) => a.localeCompare(b));

        return {
          sizes: acc.sizes,
          lengths: sortedLengths,
        };
      }

      return acc;
    },
    {
      sizes: [],
      lengths: [],
    }
  );
};

export const getProductRows = (
  products: ProductItem[],
  sections: Section[],
  sectionId: string,
  searchTerms: SearchTerm[]
): ContentItem[] => {
  let isLastRowBestMatch = true;
  let lastItems = [];
  if (sectionId) {
    const section = sections.find((section) => section.id === sectionId);
    const lastSectionItem = section.items[section.items.length - 1];
    const isLastItemProductRow =
      lastSectionItem.type === ContentItemType.ProductRow;

    isLastRowBestMatch = isLastItemProductRow
      ? lastSectionItem.isBestMatch
      : true;
    lastItems = isLastItemProductRow ? lastSectionItem.content : [];
  }

  const bestMatchesItems = [];
  const alternativesItems = [];

  if (isLastRowBestMatch === false) {
    // we in alternatives
    alternativesItems.push(...lastItems, ...products);
  } else {
    bestMatchesItems.push(...lastItems);

    let isAlternativeMet = false;
    products.forEach((product) => {
      if (isAlternativeMet) {
        alternativesItems.push(product);
        return;
      }

      const isSearchTagsConsideredAsPerfect = searchTerms.every(
        (term) =>
          term.type === SearchTermType.Text ||
          term.type === SearchTermType.Scanned ||
          term.type === SearchTermType.Size ||
          term.type === SearchTermType.Length
      );
      const isBestMatchProduct = getStyleBestMatchFlag(product);

      if (isBestMatchProduct || isSearchTagsConsideredAsPerfect) {
        bestMatchesItems.push(product);
      } else {
        isAlternativeMet = true;
        alternativesItems.push(product);
      }
    });
  }

  const bestMatchesRowsContent = chunkArray(
    bestMatchesItems,
    PRODUCT_ROW_SIZE
  ) as ProductItem[][];

  const alternativesRowsContent = chunkArray(
    alternativesItems,
    PRODUCT_ROW_SIZE
  ) as ProductItem[][];

  const bestMatchesRows: ContentItem[] = bestMatchesRowsContent.map(
    (items) => ({
      type: ContentItemType.ProductRow,
      id: nanoid(),
      isBestMatch: true,
      content: items,
    })
  );

  const alternativesRows: ContentItem[] = alternativesRowsContent.map(
    (items) => ({
      type: ContentItemType.ProductRow,
      id: nanoid(),
      isBestMatch: false,
      content: items,
    })
  );

  return [...bestMatchesRows, ...alternativesRows];
};

export const getHTWISortedMedia = (media: HTWIMedia[]) => {
  const priority = [
    PackType.TopL3,
    PackType.TopL2,
    PackType.TopL1,
    PackType.TopL0,
    PackType.OnePieceL1,
    PackType.OnePieceL0,
    PackType.TopBottom,
    PackType.BottomL1,
    PackType.BottomL0,
    PackType.Accessory,
    PackType.Multi,
    PackType.Shoes,
  ];

  return [...media].toSorted((a, b) => {
    const aPriority = priority.indexOf(a.packType);
    const bPriority = priority.indexOf(b.packType);
    return (
      (aPriority === -1 ? Infinity : aPriority) -
      (bPriority === -1 ? Infinity : bPriority)
    );
  });
};

// Collects the products that actually matched the search across the loaded
// sections — i.e. best-match ProductRows (the ones shown above "DISCOVER OTHER
// STYLES") plus any single-product PDP item. Alternatives rows (isBestMatch
// false) are excluded: they only match some of the filters, so they must not
// influence the PRICE tab. The search `totalCount` counts matches AND
// alternatives, so it cannot be used for this.
export const getMatchingProducts = (sections: Section[]): ProductItem[] => {
  const products: ProductItem[] = [];

  sections.forEach((section) => {
    section.items?.forEach((item: ContentItem) => {
      if (item.type === ContentItemType.ProductRow) {
        const row = item as ProductRowContentItem;
        if (row.isBestMatch && row.content?.length) {
          products.push(...row.content);
        }
      } else if (item.type === ContentItemType.ProductDetailsItem) {
        products.push((item as ProductContentItem).content);
      }
    });
  });

  return products;
};

// Retail prices for a set of products. A style can span a price range across
// its sizes ("100 - 200"), so both bounds are collected.
export const getProductPrices = (products: ProductItem[]): number[] => {
  const prices: number[] = [];

  products.forEach((productItem) => {
    const priceRange = getStylePriceRange(productItem?.sizeInfos);

    if (priceRange?.retailPrice) {
      const priceValues = priceRange.retailPrice
        .toString()
        .split(" - ")
        .map((price) => parseFloat(price.trim()));
      prices.push(...priceValues);
    } else if (productItem?.price?.retailPrice) {
      prices.push(productItem.price.retailPrice);
    }
  });

  return prices.filter((price) => !isNaN(price) && price > 0);
};
